/**
 * scrollWake — keeps the Unity host awake for the length of a scroll gesture.
 *
 * THE PROBLEM. The dragWake problem with scrolling in place of dragging — see dragWake.ts for the
 * governor mechanics (0.6s idle → 20fps, and on macOS a ~45ms/frame main-thread Thread.Sleep).
 * Wheel events over the embedded WebView are consumed by the WKWebView's responder chain and never
 * reach Unity's input system, plain scrolling generates no bridge traffic, and the native trackpad
 * monitor deliberately ignores scrolls over WKWebViews — so the host is throttled BEFORE the
 * gesture starts (reading the board is itself idle) and stays throttled through all of it. Every
 * wheel NSEvent must still be dequeued by the host's main run loop, so delivery chunks at the
 * throttled cadence. Measured on device (20260824, macOS standalone, 154-scene board): wheel
 * events reaching the page at 26–32/sec against a 60–120/sec trackpad stream, page rAF 25–32/sec,
 * delivery-age spikes of 210–265ms, host heartbeat 50–68ms mean / ~97ms max frames — scrolling
 * rendered at ~28fps with fifth-of-a-second hitches. iOS/iPadOS never see this: the macOS
 * main-thread sleep is macOS-only and UIKit pan-scrolling is serviced out of process.
 *
 * THE FIX. Same shape as dragWake: a payload-free `scrollActivity` ping, throttled to one per
 * 300ms, for as long as wheel/scroll events flow. Receipt alone wakes the host (both webview hosts
 * RegisterUserActivity on EVERY inbound message before dispatch); the verb itself is a recognised
 * no-op. Event-driven end to end — nothing here polls, ticks, or holds a rAF loop, so the idle
 * cost is zero and the governor's idle guarantee (the 100%-CPU problem it exists to prevent) is
 * untouched. Full rate is held only while events flow, plus the governor's own 0.6s tail.
 *
 * LISTENER SHAPE. `scroll` must be CAPTURE: it does not bubble, so a bubble-phase window listener
 * would never hear the board's `.board` scroller (precedent: the drop-ring re-placement listener
 * in SceneBoardExpanded). `wheel` is captured too for uniformity, and covers the wheel-at-a-limit
 * case where no scroll event fires — the user's fingers are on the trackpad either way. Both
 * passive: this module observes the scroll path it exists to unblock, so it must never be able to
 * block it. Momentum needs no special case — WebKit dispatches momentum-phase wheel events and the
 * scroller emits scroll events for as long as it moves, so the wake rides the whole tail.
 *
 * Residual, shared with dragWake: only TimelineWebViewPanel and PurchasingWebViewPopupPanel
 * recognise the verb (they are also the two that wake on receipt). On the other embedded hosts an
 * in-flight gesture logs an unhandled-verb line ~3/sec and gets no wake — same standing gap as
 * dragActivity, one fix when those hosts grow the receipt intercept.
 */

import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/transport';

/** One send per this many ms while a gesture is in flight. Same figure and same reasoning as
 *  dragWake.WAKE_INTERVAL_MS: 2x headroom inside the host governor's 0.6s idle window, ~3 tiny
 *  messages/sec against a 60–120/sec event stream. */
const WAKE_INTERVAL_MS = 300;

let lastSentTs = 0;

const wakeOnScrollish = (): void => {
  if (Date.now() - lastSentTs < WAKE_INTERVAL_MS) return;
  lastSentTs = Date.now();
  enqueueSendToNative('scrollActivity', {});
};

/** Installs the keepalive on `window`. No-op without the bridge: standalone and the debug harness
 *  have no host to wake, and sends there would be dead weight. Returns an uninstall function —
 *  used by the tests; production never uninstalls. */
export function installScrollWakeKeepalive(): () => void {
  if (!capabilities.bridge) return () => {};

  lastSentTs = 0; // a fresh install always wakes on its first event
  window.addEventListener('wheel', wakeOnScrollish, { capture: true, passive: true });
  window.addEventListener('scroll', wakeOnScrollish, { capture: true, passive: true });
  return () => {
    window.removeEventListener('wheel', wakeOnScrollish, true);
    window.removeEventListener('scroll', wakeOnScrollish, true);
  };
}
